import { Controller } from "@hotwired/stimulus"

/**
 * 5-step wizard for sending GO4SPILOT communications.
 *
 * Steps:
 *   1. Communication type  (card selection)
 *   2. Recipients           (audience + target cards)
 *   3. Content              (subject + body fields)
 *   4. Sending              (SMS + priority)
 *   5. Confirmation         (summary)
 */
export default class extends Controller {
  static targets = [
    "step",
    "sidebar",
    "progress",
    "progressLabel",
    "footer",
    "prevBtn",
    "submitBtn",
    "nextBtn",
    // Hidden inputs
    "commTypeInput",
    "audienceInput",
    "targetInput",
    "priorityInput",
    // Conditional panels (step 2)
    "citySelect",
    "regionSelect",
    "departmentSelect",
    "schoolSelect",
    // Step 3 fields
    "subjectField",
    "bodyField",
    // Step 4 SMS
    "smsToggle",
    "smsFields",
    "smsBody",
    "smsCharCount",
    "smsSegmentCount",
    // Step 5 summary
    "summaryType",
    "summaryAudience",
    "summaryTarget",
    "summarySubject",
    "summaryBody",
    "summaryPriority",
    "summarySms",
  ]

  static values = {
    current: { type: Number, default: 1 },
    total: { type: Number, default: 5 },
    smsBalance: { type: Number, default: 0 },
  }

  connect() {
    this.selections = {
      commType: null,
      audience: null,
      target: "all",
      priority: "normal",
    }

    this._render()
  }

  // ──── Navigation ────
  next() {
    if (!this._validateStep(this.currentValue)) return
    if (this.currentValue < this.totalValue) {
      this.currentValue++
      if (this.currentValue === this.totalValue) {
        this._updateSummary()
      }
      this._render()
    }
  }

  prev() {
    if (this.currentValue > 1) {
      this.currentValue--
      this._render()
    }
  }

  goToStep(event) {
    const target = parseInt(event.currentTarget.dataset.step, 10)
    if (target < this.currentValue) {
      this.currentValue = target
      this._render()
    }
  }

  // ──── Card Selection ────
  selectCard(event) {
    const card = event.currentTarget
    const group = card.dataset.group
    const value = card.dataset.value
    const selectedClass = "g4sp-wf-audience-card--selected"

    // Determine the correct selected class based on group
    const cssClass = group === "commType"
      ? "g4sp-wf-card--selected"
      : selectedClass

    // Deselect siblings
    const container = card.closest("[data-card-group]")
    if (container) {
      container.querySelectorAll("[data-group]").forEach((c) => {
        c.classList.remove(cssClass)
        const check = c.querySelector(".g4sp-wf-card-check")
        if (check) check.style.display = "none"
      })

      card.classList.add(cssClass)
      const check = card.querySelector(".g4sp-wf-card-check")
      if (check) check.style.display = ""
    }

    switch (group) {
      case "commType":
        this.selections.commType = value
        if (this.hasCommTypeInputTarget) {
          this.commTypeInputTarget.value = value
        }
        break
      case "audience":
        this.selections.audience = value
        if (this.hasAudienceInputTarget) {
          this.audienceInputTarget.value = value
        }
        break
      case "target":
        this.selections.target = value
        if (this.hasTargetInputTarget) {
          this.targetInputTarget.value = value
        }
        this._toggleTargetPanels()
        break
      case "priority":
        this.selections.priority = value
        if (this.hasPriorityInputTarget) {
          this.priorityInputTarget.value = value
        }
        break
    }
  }

  // ──── Target panels ────
  _toggleTargetPanels() {
    const panels = {
      city: this.hasCitySelectTarget
        ? this.citySelectTarget
        : null,
      region: this.hasRegionSelectTarget
        ? this.regionSelectTarget
        : null,
      department: this.hasDepartmentSelectTarget
        ? this.departmentSelectTarget
        : null,
      specific: this.hasSchoolSelectTarget
        ? this.schoolSelectTarget
        : null,
    }

    // Hide all
    Object.values(panels).forEach((el) => {
      if (el) el.style.display = "none"
    })

    // Show selected
    const target = this.selections.target
    if (panels[target]) {
      panels[target].style.display = ""
    }
  }

  // ──── SMS ────
  toggleSms() {
    if (!this.hasSmsFieldsTarget) return
    const enabled = this.hasSmsToggleTarget &&
      this.smsToggleTarget.checked
    this.smsFieldsTarget.style.display = enabled ? "" : "none"
  }

  updateSmsCounter() {
    if (!this.hasSmsBodyTarget) return
    const text = this.smsBodyTarget.value
    const chars = text.length
    const segments = chars <= 160
      ? (chars > 0 ? 1 : 0)
      : Math.ceil(chars / 153)

    if (this.hasSmsCharCountTarget) {
      this.smsCharCountTarget.textContent = chars
    }
    if (this.hasSmsSegmentCountTarget) {
      this.smsSegmentCountTarget.textContent = segments
    }
  }

  // ──── Validation ────
  _validateStep(step) {
    switch (step) {
      case 1:
        if (!this.selections.commType) {
          this._shake(
            this.element.querySelector(
              '[data-card-group="commType"]'
            )
          )
          return false
        }
        return true
      case 2:
        if (!this.selections.audience) {
          this._shake(
            this.element.querySelector(
              '[data-card-group="audience"]'
            )
          )
          return false
        }
        return true
      case 3: {
        const subject = this.hasSubjectFieldTarget
          ? this.subjectFieldTarget.value.trim()
          : ""
        const body = this.hasBodyFieldTarget
          ? this.bodyFieldTarget.value.trim()
          : ""
        if (!subject) {
          this._shake(
            this.subjectFieldTarget?.closest("div")
          )
          return false
        }
        if (!body) {
          this._shake(
            this.bodyFieldTarget?.closest("div")
          )
          return false
        }
        return true
      }
      case 4:
        return true
      case 5:
        return true
      default:
        return true
    }
  }

  disconnect() {
    if (this._shakeTimeout) {
      clearTimeout(this._shakeTimeout)
      this._shakeTimeout = null
    }
  }

  _shake(el) {
    if (!el) return
    el.classList.add("g4sp-wf-shake")
    this._shakeTimeout = setTimeout(
      () => el.classList.remove("g4sp-wf-shake"),
      600
    )
  }

  // ──── Summary ────
  _updateSummary() {
    // Type
    if (this.hasSummaryTypeTarget) {
      const sel = this.element.querySelector(
        '[data-card-group="commType"]' +
          " .g4sp-wf-card--selected [data-label]"
      )
      this.summaryTypeTarget.textContent =
        sel?.textContent || "\u2014"
    }

    // Audience
    if (this.hasSummaryAudienceTarget) {
      const sel = this.element.querySelector(
        '[data-card-group="audience"]' +
          " .g4sp-wf-audience-card--selected [data-label]"
      )
      this.summaryAudienceTarget.textContent =
        sel?.textContent || "\u2014"
    }

    // Target
    if (this.hasSummaryTargetTarget) {
      const sel = this.element.querySelector(
        '[data-card-group="target"]' +
          " .g4sp-wf-audience-card--selected [data-label]"
      )
      this.summaryTargetTarget.textContent =
        sel?.textContent || "\u2014"
    }

    // Subject
    if (this.hasSummarySubjectTarget &&
        this.hasSubjectFieldTarget) {
      this.summarySubjectTarget.textContent =
        this.subjectFieldTarget.value.trim() || "\u2014"
    }

    // Body
    if (this.hasSummaryBodyTarget &&
        this.hasBodyFieldTarget) {
      const val = this.bodyFieldTarget.value.trim()
      this.summaryBodyTarget.textContent =
        val
          ? (val.length > 200
              ? val.substring(0, 200) + "\u2026"
              : val)
          : "\u2014"
    }

    // Priority
    if (this.hasSummaryPriorityTarget) {
      const sel = this.element.querySelector(
        '[data-card-group="priority"]' +
          " .g4sp-wf-audience-card--selected [data-label]"
      )
      this.summaryPriorityTarget.textContent =
        sel?.textContent || "\u2014"
    }

    // SMS
    if (this.hasSummarySmsTarget) {
      const enabled = this.hasSmsToggleTarget &&
        this.smsToggleTarget.checked
      this.summarySmsTarget.textContent = enabled
        ? "\u2713"
        : "\u2014"
    }
  }

  // ──── Render ────
  _render() {
    const step = this.currentValue
    const pct = Math.round(
      (step / this.totalValue) * 100
    )

    // Show/hide step panels
    this.stepTargets.forEach((panel) => {
      const s = parseInt(panel.dataset.step, 10)
      panel.style.display = s === step ? "" : "none"
    })

    // Update sidebar
    this.sidebarTargets.forEach((item) => {
      const s = parseInt(item.dataset.step, 10)
      item.classList.remove(
        "g4sp-wf-step--active",
        "g4sp-wf-step--done",
        "g4sp-wf-step--disabled"
      )
      if (s === step) {
        item.classList.add("g4sp-wf-step--active")
      } else if (s < step) {
        item.classList.add("g4sp-wf-step--done")
      } else {
        item.classList.add("g4sp-wf-step--disabled")
      }
    })

    // Progress bar
    if (this.hasProgressTarget) {
      this.progressTarget.style.width = pct + "%"
    }
    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent =
        `${step} / ${this.totalValue} \u2014 ${pct} %`
    }

    // Footer breadcrumb
    if (this.hasFooterTarget) {
      const active = this.sidebarTargets.find(
        (i) =>
          parseInt(i.dataset.step, 10) === step
      )
      if (active) {
        const title =
          active.querySelector("[data-step-title]")
            ?.textContent || ""
        const sub =
          active.querySelector("[data-step-sub]")
            ?.textContent || ""
        this.footerTarget.textContent = ''
        const strong = document.createElement('strong')
        strong.textContent = title
        this.footerTarget.appendChild(strong)
        const span = document.createElement('span')
        span.textContent = ` \u2014 ${sub}`
        this.footerTarget.appendChild(span)
      }
    }

    // Prev/Next/Submit visibility
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.style.display =
        step > 1 ? "" : "none"
    }
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.style.display =
        step < this.totalValue ? "" : "none"
    }
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.style.display =
        step === this.totalValue ? "" : "none"
    }
  }
}
